import { useState } from 'react';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import { useNavigate, useParams } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Alert, Button, Typography } from '@mui/material';
import CostOfWork from './CostOfWork';
import { handleInsertSubcodeDocs } from '../uploadSubcodeDocs';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import {
  resetUccForm,
  updateElectrical
} from 'src/redux-store/slices/constructionFormSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';
import Summary from './Summary';
import getEnvVars from 'src/config/env';

// Interface for tab panel props
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Custom tab panel component
function CustomTabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Function to generate accessibility props for tabs
type A11yProps = (index: number) => { id: string; 'aria-controls': string };
const a11yProps: A11yProps = (index) => ({
  id: `simple-tab-${index}`,
  'aria-controls': `simple-tabpanel-${index}`
});

const ElectricalSubcode = ({
  applicationId,
  UCCFormShareID = 0,
  townshipID = 0,
  setExpanded,
  isGuest = false,
  fetchFormStatuses,
}) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(0);
  const { applicationID, townshipId } = useParams();
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const subCodesFormData = useSelector(
    (state: RootState) => state.constructionFormSlice.Electrical
  );
  const { socketUrl } = getEnvVars();
  // Handles form field changes and updates the redux store
  const handleChange = (val: string, e: any) => {
    const { name } = e;
    dispatch(updateElectrical({ [name]: val }));
  };
  const navigate = useNavigate()

  // List of form fields
  const allFields: Fields[] = [
    {
      fieldName: 'ElectricianName',
      initialValue: subCodesFormData?.ElectricianName || '',
      fieldLabel: 'Electrician Name',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ElectricianEmail',
      initialValue: subCodesFormData?.ElectricianEmail || '',
      fieldLabel: 'Electrician Email Address',
      fieldType: 'email',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ElectricianPhoneNumber',
      initialValue: subCodesFormData?.ElectricianPhoneNumber || '',
      fieldLabel: 'Electrician Phone Number',
      fieldType: 'mobile',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'UtilityCompany',
      initialValue: subCodesFormData?.UtilityCompany || '',
      fieldLabel: 'Utility Company',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'FederalID',
      initialValue: subCodesFormData?.FederalID || '',
      fieldLabel: 'Federal Employer ID Number (EIN)',
      fieldType: 'number',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ContractorLicenseNumber',
      initialValue: subCodesFormData?.ContractorLicenseNumber || '',
      fieldLabel: 'Contractor License Number',
      fieldType: 'text',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ElectricianLicenseExpiryDate',
      initialValue: subCodesFormData?.ElectricianLicenseExpiryDate || '',
      fieldLabel: 'Electrician License Expiration Date',
      required: true,
      fieldType: 'date',
      dependency: (val: string) =>
        dispatch(updateElectrical({ ElectricianLicenseExpiryDate: val }))
    },
    {
      fieldName: 'DescriptionOfWork',
      initialValue: subCodesFormData?.DescriptionOfWork || '',
      fieldLabel: 'Description of Work',
      fieldType: 'note',
      required: true,
      dependency: handleChange
    },
    {
      fieldName: 'ElectricalDocumentFile',
      fieldLabel: 'Upload Document',
      initialValue: subCodesFormData?.ElectricalDocumentFile || '',
      fieldType: 'file',
      listUI: true,
      placement: 'start',
      dependency: handleChange,
      aboveParagraph: (
        <Typography>
          Please upload scanned copy or photo of the Federal Employer ID Number
          (EIN) license. Only image files (JPG, PNG, GIF, SVG, HEIC) and PDF
          files are allowed, with maximum file size of 10MB.
        </Typography>
      ),
      belowParagraph: subCodesFormData?.DocumentOfElectricianIdentity && (
        <a
          target="_blank"
          rel="noreferrer"
          href={socketUrl + subCodesFormData?.DocumentOfElectricianIdentity}
        >
          Federal Employer ID Number (EIN) license
        </a>
      )
    }
  ];

  async function insertElectricalSubcode(payload: any) {
    const response = await httpRequest(
      'POST',
      `/construction/electrical/ucc/${applicationId}`,
      payload
    );
    if (response?.status === 'success') {
      toast.success(`Electrical UCC form created successfully.`);
      dispatch(
        updateElectrical({ UCCElectricalID: response?.data?.UCCElectricalID })
      );
      if (!townshipID) fetchFormStatuses();
      dispatch(resetUccForm());
      if (isGuest === true) {
        // For guest users, redirect to confirmation page
        // You may need to adjust the route based on your routing setup
        navigate('/confirmation', { 
          state: { 
            formType: 'Electrical UCC Form',
            email: payload.email 
          },
          replace: true // This prevents going back with the browser back button
        });
      } else {
        // For regular users, just reset the form
        setValue(0);
      }
      // setLoading(false);
    } else {
      // setLoading(false);
      toast.error(response?.message);
    }
  }

  async function updateElectricalSubcode(payload: any) {
    const response = await httpRequest(
      'PATCH',
      `/construction/electrical/ucc/${subCodesFormData?.UCCElectricalID}/${
        applicationId || applicationID
      }`,
      payload
    );
    if (response?.status === 'success') {
      toast.success(`Electrical UCC form updated successfully.`);
      dispatch(
        updateElectrical({ UCCElectricalID: response?.data?.UCCElectricalID })
      );
      if (!townshipID) fetchFormStatuses();
      dispatch(resetUccForm());
      setValue(0);
      // setLoading(false);
    } else {
      // setLoading(false);
      toast.error(response?.message);
    }
  }

  async function handleSaveForm() {
    let FileData = { FilePath: '' };
    if (subCodesFormData?.ElectricalDocumentFile) {
      FileData = await handleInsertSubcodeDocs(
        subCodesFormData?.ElectricalDocumentFile[0],
        'Construction',
        'Electrical'
      );
    }
    const payload = {
      ElectricianName: subCodesFormData?.ElectricianName,
      ElectricianEmail: subCodesFormData?.ElectricianEmail,
      ElectricianPhoneNumber: subCodesFormData?.ElectricianPhoneNumber,
      UtilityCompany: subCodesFormData?.UtilityCompany,
      FederalID: subCodesFormData?.FederalID,
      ContractorLicenseNumber: subCodesFormData?.ContractorLicenseNumber,
      ElectricianLicenseExpiryDate:
        subCodesFormData?.ElectricianLicenseExpiryDate,
      DescriptionOfWork: subCodesFormData?.DescriptionOfWork,
      EstimatedCostOfElectricWork: subCodesFormData?.EstimatedCostOfElectricWork,
      SignatureImage: subCodesFormData?.SignatureImage,
      TotalFees: subCodesFormData?.getAllFee?.totalFee,
      WorkItems: subCodesFormData?.WorkItems || [],
      DocumentOfElectricianIdentity: FileData?.FilePath,
      SelfFilled: userDetails?.UserID ? true : false,
      UserID: userDetails?.UserID || 0,
      TownshipID: townshipId || townshipID,
      UCCFormShareID: UCCFormShareID
    };
    if (subCodesFormData?.UCCElectricalID) {
      updateElectricalSubcode(payload);
    } else insertElectricalSubcode(payload);
    setValue(2);
  }

  function onNext() {
    setValue(value + 1);
  }

  function onBack() {
    setValue(value - 1);
  }
  // Add this function after your existing handleChange function
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
  setValue(newValue);
  };

  return (
    <>
      <Box sx={{ width: '100%' }}>
        {/* Tabs */}
        <Tabs value={value} onChange={handleTabChange} aria-label="Construction Permit Tabs">
          <Tab label="Electrical Subcode" {...a11yProps(0)} />
          <Tab label="Cost of Work" {...a11yProps(1)} />
          <Tab label="Summary of Costs" {...a11yProps(2)} />
        </Tabs>

        <Alert severity="info" sx={{ mt: 2 }}>
          Provide information for whoever will be performing Electrical work.
        </Alert>

        {/* Tab 1 - Electrical Subcode Form */}
        <CustomTabPanel value={value} index={0}>
          <FormikForm
            allFields={allFields}
            defaultEnabledButton={['Save']}
            buttonTitle={['Cancel', 'Save']}
            heading={'Construction Permit'}
            buttonPlacement={'right'}
            formMethod={onNext}
            handleOtherButton={() => setExpanded(false)}
          />
        </CustomTabPanel>

        {/* Tab 2 - Estimated Cost of Work Form */}
        <CustomTabPanel value={value} index={1}>
          <CostOfWork
            estimatedCostOfWork={subCodesFormData?.EstimatedCostOfElectricWork}
            onBack={onBack}
            onNext={onNext}
            townshipID={townshipID}
          />
        </CustomTabPanel>

        {/* Tab 3 - Summary of Costs */}
        <CustomTabPanel value={value} index={2}>
          <Summary />

          {/* Form Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 3
            }}
          >
            <Button onClick={onBack} variant="contained" color="inherit">
              Back
            </Button>
            <Button onClick={handleSaveForm} variant="contained">
              {subCodesFormData?.UCCElectricalID ? 'Update' : 'Save'}
            </Button>
          </Box>
        </CustomTabPanel>
      </Box>
    </>
  );
};

// Add display name for debugging purposes
ElectricalSubcode.displayName = 'ElectricalSubcode';

export default ElectricalSubcode;
